import { resolveUsersSentinel } from '@src/hooks/filters/useUsersFilter';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { useUser } from '@src/hooks/useUser';
import {
  CARE_ORDER,
  QUOTE,
  useCombinedErrorObs,
  useSubscription,
  useWSFilterPipe,
  WORKING_ORDER,
  wsScanToMap,
  wsStitchWith,
  type CareOrder,
  type MinimalSubscriptionResponse,
  type Quote,
  type User,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { asyncScheduler, map, shareReplay, throttleTime, type Observable } from 'rxjs';
import { calculateBestQuotes, type RfqBestQuoteBy } from '../../../utils/rfq';
import {
  InitiatingFromRow,
  ParentRFQIDRow,
  QuoteMarketRow,
  QuoteRow,
  SweepGroupIDRow,
  type QuotesBlotterRow,
} from './types';
import { quotesV2ServerFilter, type QuotesV2TableFilter } from './useQuotesV2Filter';

function getQuoteKey(quote: Quote) {
  return quote.RFQID;
}

interface UseQuotesV2BlotterDataObsParams {
  filterFn: (quote: Quote) => boolean;
  filter: QuotesV2TableFilter;
  options: QuoteToRowsOptions;
}

export const useQuotesV2BlotterDataObs = ({ filterFn, filter, options }: UseQuotesV2BlotterDataObsParams) => {
  const { enableExecutingOtherUserNegotiatedRFQ, enrichRFQBlotterV2WithCareOrders, requireMobileUITraderRole } =
    useFeatureFlag();
  const user = useUser();
  const isMobileLayout = useIsMobileLayout();
  const serverFilter = useMemo(() => {
    const resolvedUsers = resolveUsersSentinel(filter.Users, user.Name);
    return quotesV2ServerFilter({ ...filter, ...(resolvedUsers !== undefined ? { Users: resolvedUsers } : {}) });
  }, [filter, user.Name]);

  const quotesRequest = useMemo(
    () => ({
      name: QUOTE,
      tag: 'useQuotesV2BlotterDataObs',
      ...serverFilter,
    }),
    [serverFilter]
  );

  const careOrdersRequest = useMemo(
    () => ({
      name: CARE_ORDER,
      tag: 'useQuotesV2BlotterDataObs',

      /* Start and End is good to add here for pagination purposes. Don't always need to load all COs */
      StartDate: serverFilter?.StartDate,
      EndDate: serverFilter?.EndDate,
    }),
    [serverFilter?.StartDate, serverFilter?.EndDate]
  );

  const workingOrdersRequest = useMemo(
    () => ({
      name: WORKING_ORDER,
      tag: 'useQuotesV2BlotterDataObs',

      /* Start and End is good to add here for pagination purposes. Don't always need to load all WorkingOrders */
      StartDate: serverFilter?.StartDate,
      EndDate: serverFilter?.EndDate,
    }),
    [serverFilter?.StartDate, serverFilter?.EndDate]
  );

  const { data: quotesObs, error: quotesError } = useSubscription<Quote>(quotesRequest);
  const { data: careOrdersObs, error: careOrdersError } = useSubscription<CareOrder>(
    enrichRFQBlotterV2WithCareOrders ? careOrdersRequest : null
  );
  const { data: workingOrdersObs, error: workingOrdersError } = useSubscription<CareOrder>(
    enrichRFQBlotterV2WithCareOrders ? workingOrdersRequest : null
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey: getQuoteKey, filterFunc: filterFn });

  const enrichedQuotesObs = useMemo(
    () =>
      quotesObs.pipe(
        filterPipe,
        wsStitchWith({
          secondarySource: careOrdersObs,
          getPrimaryTypeKey: quote => quote.RFQID, // storage key for quotes is RFQID
          getPrimaryTypeStitchKey: quote => quote.ParentRFQID, // but we stitch (match) Quote<->CO using Quote.ParentRFQID <-> CareOrder.OrderID
          getSecondaryTypeKey: careOrder => careOrder.OrderID,
          stitch: (quote, careOrder) => (careOrder ? quote.enrichWithCareOrder(careOrder) : quote),
        }),
        wsStitchWith({
          secondarySource: workingOrdersObs,
          getPrimaryTypeKey: quote => quote.RFQID,
          getPrimaryTypeStitchKey: quote => quote.ParentRFQID,
          getSecondaryTypeKey: workingOrder => workingOrder.OrderID,
          stitch: (quote, workingOrder) => (workingOrder ? quote.enrichWithCareOrder(workingOrder) : quote),
        }),
        wsScanToMap({ getUniqueKey: quote => quote.RFQID, newMapEachUpdate: false }),
        throttleTime(500, asyncScheduler, { leading: true, trailing: true }), // Streaming RFQs can be very spammy. Throttle a bit.
        shareReplay({ refCount: false, bufferSize: 1 })
      ),
    [filterPipe, quotesObs, careOrdersObs, workingOrdersObs]
  );

  const dataObservable: Observable<MinimalSubscriptionResponse<QuotesBlotterRow>> = useMemo(() => {
    return enrichedQuotesObs.pipe(
      map(mapping => {
        const includedSweepGroupIDs = new Set<string>();
        const quotes = [...mapping.values()];
        const quotesBySweepGroupID = options.groupBySweepGroupID ? getQuotesBySweepGroupID(quotes) : undefined;

        return {
          initial: true,
          data: quotes.flatMap(quote =>
            quoteToRows(
              quote,
              options,
              includedSweepGroupIDs,
              user,
              isMobileLayout,
              requireMobileUITraderRole,
              enableExecutingOtherUserNegotiatedRFQ,
              quotesBySweepGroupID
            )
          ),
        };
      })
    );
  }, [
    enableExecutingOtherUserNegotiatedRFQ,
    enrichedQuotesObs,
    isMobileLayout,
    options,
    requireMobileUITraderRole,
    user,
  ]);

  const errorObservable = useCombinedErrorObs([quotesError, careOrdersError, workingOrdersError]);
  return { dataObservable, errorObservable, quotesObs };
};

export interface QuoteToRowsOptions {
  groupByInitiatingFrom: boolean;
  groupByParentRFQID?: boolean;
  groupBySweepGroupID?: boolean;
  bestBy?: RfqBestQuoteBy;
}

function quoteToRows(
  quote: Quote,
  options: QuoteToRowsOptions,
  includedSweepGroupIDs: Set<string>,
  user: User,
  isMobile: boolean,
  requireMobileUITraderRole: boolean,
  enableExecutingOtherUserNegotiatedRFQ: boolean,
  quotesBySweepGroupID?: Map<string, Quote[]>
): QuotesBlotterRow[] {
  let dataPath: string[] = [];
  const rows: QuotesBlotterRow[] = [];

  if (options.groupBySweepGroupID) {
    dataPath = [...dataPath, SweepGroupIDRow.getPathComponent(quote)];
    const sweepGroupID = SweepGroupIDRow.getPathComponent(quote);
    if (!includedSweepGroupIDs.has(sweepGroupID)) {
      includedSweepGroupIDs.add(sweepGroupID);
      rows.push(
        new SweepGroupIDRow(
          [...dataPath],
          quotesBySweepGroupID?.get(sweepGroupID) ?? [quote],
          user,
          isMobile,
          requireMobileUITraderRole,
          enableExecutingOtherUserNegotiatedRFQ
        )
      );
    }
  }

  if (options.groupByParentRFQID) {
    dataPath = [...dataPath, ParentRFQIDRow.getPathComponent(quote)];
    rows.push(new ParentRFQIDRow([...dataPath], quote));
  }

  if (options.groupByInitiatingFrom) {
    dataPath = [...dataPath, InitiatingFromRow.getPathComponent(quote)];
    rows.push(new InitiatingFromRow([...dataPath], quote));
  }

  dataPath = [...dataPath, QuoteRow.getPathComponent(quote)];
  const quoteRow = new QuoteRow([...dataPath], quote, options.bestBy);

  const quoteMarketsWithBBO = calculateBestQuotes({
    quotes: quote.Markets,
    pricingMode: quote.PricingMode,
    bestBy: options.bestBy,
  });
  const quoteMarketRows = quoteMarketsWithBBO.map(quoteMarket => {
    return new QuoteMarketRow([...dataPath, QuoteMarketRow.getPathComponent(quote, quoteMarket)], quote, quoteMarket);
  });

  rows.push(quoteRow);
  quoteMarketRows.forEach(row => rows.push(row));
  return rows;
}

function getQuotesBySweepGroupID(quotes: Quote[]): Map<string, Quote[]> {
  return quotes.reduce((mapping, quote) => {
    const sweepGroupID = SweepGroupIDRow.getPathComponent(quote);
    const groupQuotes = mapping.get(sweepGroupID) ?? [];
    groupQuotes.push(quote);
    mapping.set(sweepGroupID, groupQuotes);
    return mapping;
  }, new Map<string, Quote[]>());
}
